import React, { useState, useEffect } from "react";
import { fetchSearchMappings, updateSearchMapping, normalizeSearchMappings, resetSearchMappings } from "../../../api/admin/searchMapping";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { SAdminSearchScore, STable, SEdit } from "../../../styles/AdminSearchScore";
import { Button } from "../../Button";
import { QueryLoader } from "../../Loader";
import SaveIcon from "../../../assets/img/icons/save.svg";
import CancelIcon from "../../../assets/img/icons/cancel.svg";
import MinusIcon from "../../../assets/img/icons/minus.svg";
import PlusIcon from "../../../assets/img/icons/plus.svg";

const MAX_SCORE = 100;

export const SearchMappingAdmin = () => {
    const { axiosPrivate } = useAxiosPrivate();
    const [mappings, setMappings] = useState([]);
    const [newWeights, setNewWeights] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        loadMappings();
    }, []);

    const loadMappings = async () => {
        try {
            setLoading(true);
            const data = await fetchSearchMappings(axiosPrivate);
            setMappings(data);
            
            // Initialize newWeights with current weights
            const weights = {};
            data.forEach(mapping => {
                weights[mapping.id] = mapping.weight;
            });
            setNewWeights(weights);
            
            setError("");
        } catch (err) {
            setError("Failed to load search mappings");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleWeightChange = (id, type) => {
        setNewWeights(prev => {
            const currentWeight = prev[id] || 0;
            let newWeight = currentWeight;
            
            switch (type) {
                case "increment":
                    if (getTotalWeight(prev) < MAX_SCORE) {
                        newWeight = currentWeight + 1;
                    }
                    break;
                case "decrement":
                    if (currentWeight > 0) {
                        newWeight = currentWeight - 1;
                    }
                    break;
            }
            
            return { ...prev, [id]: newWeight };
        });
    };

    const handleDirectWeightChange = (id, value) => {
        const numValue = parseInt(value) || 0;
        if (numValue >= 0) {
            setNewWeights(prev => ({ ...prev, [id]: numValue }));
        }
    };

    const getTotalWeight = (weights = newWeights) => {
        return Object.values(weights).reduce((sum, weight) => sum + (weight || 0), 0);
    };

    const getCurrentTotalWeight = () => {
        if (!Array.isArray(mappings)) return 0;
        return mappings.filter(m => m.isActive).reduce((sum, m) => sum + m.weight, 0);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            
            if (!Array.isArray(mappings)) {
                setError("Invalid mappings data");
                return;
            }
            
            // Update each mapping that has changed
            const updatePromises = mappings.map(async (mapping) => {
                const newWeight = newWeights[mapping.id];
                if (newWeight !== mapping.weight) {
                    await updateSearchMapping(axiosPrivate, mapping.id, { 
                        weight: newWeight,
                        isActive: mapping.isActive,
                        description: mapping.description 
                    });
                }
            });
            
            await Promise.all(updatePromises);
            
            // Reload mappings to get updated data
            await loadMappings();
            
            setMessage("Weights updated successfully");
            setError("");
            setTimeout(() => setMessage(""), 3000);
        } catch (err) {
            setError("Failed to update weights");
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const handleDiscard = () => {
        if (!Array.isArray(mappings)) return;
        
        // Reset newWeights to current mapping weights
        const weights = {};
        mappings.forEach(mapping => {
            weights[mapping.id] = mapping.weight;
        });
        setNewWeights(weights);
        setMessage("");
        setError("");
    };

    const handleNormalize = async () => {
        try {
            setSaving(true);
            const response = await normalizeSearchMappings(axiosPrivate);
            console.log("Normalize response:", response);
            
            // Reload mappings from server to get updated data
            await loadMappings();
            
            setMessage("Weights normalized to total 100%");
            setError("");
            setTimeout(() => setMessage(""), 3000);
        } catch (err) {
            setError("Failed to normalize weights");
            console.error("Normalize error:", err);
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm("Are you sure you want to reset all weights to default values?")) {
            return;
        }

        try {
            setSaving(true);
            const response = await resetSearchMappings(axiosPrivate);
            console.log("Reset response:", response);
            
            // Reload mappings from server to get updated data
            await loadMappings();
            
            setMessage("Weights reset to default values");
            setError("");
            setTimeout(() => setMessage(""), 3000);
        } catch (err) {
            setError("Failed to reset weights");
            console.error("Reset error:", err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <QueryLoader />;
    }

    if (!Array.isArray(mappings)) {
        return (
            <div style={{ padding: "20px", textAlign: "center" }}>
                <p>Error: Invalid mappings data. Please refresh the page.</p>
                <button onClick={loadMappings}>Retry</button>
            </div>
        );
    }

    const currentTotal = getCurrentTotalWeight();
    const newTotal = getTotalWeight();
    const hasChanges = Array.isArray(mappings) && mappings.some(mapping => newWeights[mapping.id] !== mapping.weight);

    return (
        <SAdminSearchScore>
            {message && (
                <div style={{
                    padding: "10px",
                    marginBottom: "15px",
                    backgroundColor: "#d4edda",
                    color: "#155724",
                    border: "1px solid #c3e6cb",
                    borderRadius: "4px"
                }}>
                    {message}
                </div>
            )}
            
            {error && (
                <div style={{
                    padding: "10px",
                    marginBottom: "15px",
                    backgroundColor: "#f8d7da",
                    color: "#721c24",
                    border: "1px solid #f5c6cb",
                    borderRadius: "4px"
                }}>
                    {error}
                </div>
            )}

            <STable>
                <thead>
                    <tr>
                        <td>Factor</td>
                        <td>Relevance</td>
                        <td>Update</td>
                    </tr>
                </thead>
                <tbody>
                    {mappings
                        .filter(mapping => mapping.isActive)
                        .sort((a, b) => {
                            // Sort by the number prefix in description (1., 2., 3., etc.)
                            const aOrder = parseInt(a.description.match(/^(\d+)\./)?.[1] || "999");
                            const bOrder = parseInt(b.description.match(/^(\d+)\./)?.[1] || "999");
                            return aOrder - bOrder;
                        })
                        .map((mapping) => (
                        <tr key={mapping.id}>
                            <td>
                                {/* Remove the number prefix for display */}
                                {mapping.description.replace(/^\d+\.\s*/, "")}
                            </td>
                            <td>{mapping.weight}</td>
                            <td>
                                <SEdit>
                                    <button 
                                        onClick={() => handleWeightChange(mapping.id, "decrement")}
                                        disabled={newWeights[mapping.id] <= 0}
                                    >
                                        <img src={MinusIcon} alt="decrease" />
                                    </button>
                                    <input
                                        type="text"
                                        value={newWeights[mapping.id] || 0}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/[^0-9]/g, ""); // Only allow numbers
                                            handleDirectWeightChange(mapping.id, value);
                                        }}
                                        style={{
                                            width: "3rem",
                                            textAlign: "center",
                                            border: "none",
                                            outline: "none",
                                            padding: "2px",
                                            margin: "0",
                                            fontSize: "14px",
                                            backgroundColor: "transparent"
                                        }}
                                        onKeyDown={(e) => {
                                            // Allow: backspace, delete, tab, escape, enter, home, end, left, right
                                            if ([46, 8, 9, 27, 13, 110, 35, 36, 37, 39].indexOf(e.keyCode) !== -1 ||
                                                // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                                                (e.keyCode === 65 && e.ctrlKey === true) ||
                                                (e.keyCode === 67 && e.ctrlKey === true) ||
                                                (e.keyCode === 86 && e.ctrlKey === true) ||
                                                (e.keyCode === 88 && e.ctrlKey === true)) {
                                                return;
                                            }
                                            // Ensure that it is a number and stop the keypress
                                            if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
                                                e.preventDefault();
                                            }
                                        }}
                                    />
                                    <button 
                                        onClick={() => handleWeightChange(mapping.id, "increment")}
                                        disabled={newTotal >= MAX_SCORE}
                                    >
                                        <img src={PlusIcon} alt="increase" />
                                    </button>
                                </SEdit>
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td>Total:</td>
                        <td>{currentTotal}</td>
                        <td>{newTotal}</td>
                    </tr>
                </tfoot>
            </STable>

            <Button
                icon={CancelIcon}
                onClick={handleDiscard}
                disabled={saving || !hasChanges}
            >
                Discard
            </Button>
            
            <Button
                icon={SaveIcon}
                onClick={handleSave}
                disabled={saving || !hasChanges || newTotal !== MAX_SCORE}
            >
                {saving ? "Saving..." : "Save"}
            </Button>
        </SAdminSearchScore>
    );
};
